import { db, workspaceAccount } from './db.js';
import { attemptSessionRenewal, clearAuthSession, getAuthToken } from './auth.js';
import { WorkspaceSyncEngine } from './workspaceSyncEngine.js';

export type SyncTriggerSource = 'startup' | 'interval' | 'online' | 'mutation' | 'foreground';
export class SyncService {
  private static running: Promise<void> | null = null;
  private static pending = false;
  private static listeners = new Set<(active: boolean) => void>();
  private static counters = { startup: 0, interval: 0, online: 0, mutation: 0, foreground: 0 };
  static getTelemetryCounters() { return { ...this.counters }; }
  static subscribe(listener: (active: boolean) => void) { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }
  static requestSync(source: SyncTriggerSource, _options?: { urgentMutationFlush?: boolean }) { this.counters[source]++; return this.sync(); }
  static sync(): Promise<void> {
    this.pending = true;
    if (this.running) return this.running;
    this.running = this.run().finally(() => { this.running = null; });
    return this.running;
  }
  private static async run() {
    this.listeners.forEach(listener => listener(true));
    try {
      do {
        this.pending = false;
        const database = db, account = workspaceAccount;
        if (!account || !getAuthToken() || !navigator.onLine) continue;
        const current = () => db === database && workspaceAccount === account;
        const work = async () => {
          if (!current()) return;
          // A database lease protects browsers without Web Locks and recovers
          // an abandoned lock after a closed/crashed tab.
          const owner = crypto.randomUUID();
          const acquired = await database.transaction('rw', database.workspaceMeta, async () => {
            const lease = await database.workspaceMeta.get('sync-lease');
            if (lease?.value?.until > Date.now()) return false;
            await database.workspaceMeta.put({ key: 'sync-lease', value: { owner, until: Date.now() + 90000 } });
            return true;
          });
          if (!acquired) return;
          const heartbeat = setInterval(() => void database.transaction('rw', database.workspaceMeta, async () => {
            const lease = await database.workspaceMeta.get('sync-lease');
            if (lease?.value?.owner === owner) await database.workspaceMeta.put({ key: 'sync-lease', value: { owner, until: Date.now() + 90000 } });
          }), 20000);
          try {
            const request = async (path: string, body?: any, renewed = false): Promise<Response> => {
              if (!current()) throw new Error('Workspace changed; sync stopped.');
              const token = getAuthToken();
              if (!token) throw new Error('Sign in to sync your saved edits.');
              const controller = new AbortController();
              const timer = setTimeout(() => controller.abort(), 25000);
              const accountChanged = () => { if (!current()) controller.abort(); };
              window.addEventListener('sonata-auth-changed', accountChanged);
              try {
                const runtimeBase = (window as any).process?.env?.VITE_API_URL;
                const base = runtimeBase && !runtimeBase.startsWith('__') ? runtimeBase : (import.meta as any).env?.VITE_API_URL || '';
                const response = await fetch(`${base}${path}`, { method: body ? 'POST' : 'GET', signal: controller.signal,
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
                if (!current()) throw new Error('Workspace changed; sync stopped.');
                if (response.status === 401 && !renewed) {
                  if (await attemptSessionRenewal()) return request(path, body, true);
                  if (current() && getAuthToken() === token) clearAuthSession();
                }
                return response;
              } finally { clearTimeout(timer); window.removeEventListener('sonata-auth-changed', accountChanged); }
            };
            const engine = new WorkspaceSyncEngine(database, account, request, current);
            await engine.recoverInterrupted(); await engine.push(); await engine.pull();
            if (current()) {
              await database.workspaceMeta.put({ key: 'sync-error', value: null });
              await database.workspaceMeta.put({ key: 'sync-last-confirmed', value: Date.now() });
            }
          } catch (error) {
            await database.workspaceMeta.put({ key: 'sync-error', value: (error as Error).message });
          } finally {
            clearInterval(heartbeat);
            await database.transaction('rw', database.workspaceMeta, async () => {
              const lease = await database.workspaceMeta.get('sync-lease');
              if (lease?.value?.owner === owner) await database.workspaceMeta.delete('sync-lease');
            });
          }
        };
        if (navigator.locks) await navigator.locks.request(`sonata-sync:${database.name}`, work);
        else await work();
      } while (this.pending);
    } finally { this.listeners.forEach(listener => listener(false)); }
  }
}
